import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useSWR, { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { PaginatedResponse } from '/@/types/core';
import { useIsValidatedAdvisor, useIsValidatedBayerUserType, useIsValidatedRV } from '../session';

// Types based on backend serializers
interface UnassignedUserData {
    id?: number;
    name?: string;
    email?: string;
    phone?: string;
    location?: string;
}

export interface User {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    phone?: string;
    authProviders?: string[];
}

export interface UserNode {
    id: number;
    name: string;
    email: string;
    phone?: string;
    location?: string;
    parent?: number; // FIXME: `parent` is deprecated, use `parents?.[0]` instead
    parents?: number[];
    type: 'rv' | 'grower' | 'consultant' | 'other' | 'advisor' | 'squad' | 'squad_coach';
    subtype?: 'common' | 'premium' | null;
    unassignedUserData?: UnassignedUserData;
    user: User | null;
    created: string;
    cuit?: string;
}

interface CreateUserNodeRequest {
    type: 'rv' | 'grower' | 'consultant' | 'other';
    unassignedUserData?: UnassignedUserData;
    /** CUIT digits (11) or formatted; backend normalizes to digits */
    cuit?: string;
    /** Parent user node IDs; backend defaults to [current user's node] if omitted */
    parents?: number[];
    /** Grower subtype: 'premium' | 'common'; backend may override for Advisor/RV links */
    subtype?: string;
}

interface UpdateUserNodeRequest {
    type?: 'rv' | 'grower' | 'consultant' | 'other';
    subtype?: 'common' | 'premium';
    unassignedUserData?: Partial<UnassignedUserData>;
    /** Parent user node IDs; only allowed when RV updates a grower (allow_rv_set_grower_parents). */
    parents?: number[];
    /** CUIT digits (11) or formatted; backend normalizes to digits. */
    cuit?: string;
}

export interface Invitation {
    id: number;
    invited_user: User;
    user: User;
    status: 'pending' | 'accepted';
    created: string;
    updated: string;
    expires: string;
    invited_user_email: string;
    invited_user_name: string;
}

// Advisor-RV API types (backend: UserNodeSummarySerializer, RVAdvisorLinkSerializer, RVAdvisorInvitationSerializer)

export interface RVAdvisorInvitationApi {
    id: number;
    advisor: ReturnType<typeof parseUserNode>;
    rv: ReturnType<typeof parseUserNode>;
    invitedBy: 'advisor' | 'rv';
    status: 'pending' | 'accepted' | 'declined' | 'cancelled';
    created: string;
    modified: string;
}

export interface RVAdvisorLinkApi {
    id: number;
    advisor: ReturnType<typeof parseUserNode>;
    rv: ReturnType<typeof parseUserNode>;
    created: string;
    modified: string;
}

// Utils

const getUserFullName = (user: User | null): string => (user ? `${user.firstName} ${user.lastName}`.trim() : '');

export const parseUserNode = (userNode: UserNode, user?: User) => {
    const isSelf = Boolean(user && user.email === userNode.user?.email);
    const ud = userNode.unassignedUserData;
    const u = userNode.user;

    const useUserData = isSelf || !ud;

    const name = useUserData ? getUserFullName(u) : ud?.name?.trim() || getUserFullName(u);
    const email = useUserData ? (u?.email ?? '') : (ud?.email ?? u?.email ?? '');
    const phone = useUserData ? u?.phone : (ud?.phone ?? u?.phone);
    const location = useUserData ? undefined : ud?.location;

    return {
        ...userNode,
        name,
        email,
        phone,
        location,
        isSelf,
    };
};

// API Providers
const getUserNodesFromApi = async (
    {
        node,
        type,
        parent,
        parent__in,
        page = 1,
        page_size = 10,
        includeChildren = true,
        excludeTargetNode = false,
    }: {
        node?: number;
        type?: string;
        parent?: number;
        parent__in?: number[];
        page?: number;
        page_size?: number;
        includeChildren?: boolean;
        excludeTargetNode?: boolean;
    },
    user: User
): Promise<PaginatedResponse<ReturnType<typeof parseUserNode>>> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/nodes/usernodes/`, window.location.origin);

    const params = new URLSearchParams();
    if (node) params.append('node', String(node));
    if (type) params.append('type', type);
    if (parent !== undefined) params.append('parent', String(parent));
    if (parent__in && Array.isArray(parent__in)) {
        params.append('parent__in', parent__in.join(','));
    }
    params.append('page', String(page));
    params.append('page_size', String(page_size));
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    if (res.status === 200) {
        return {
            ...res.data,
            results: res.data.results.map((userNode) => parseUserNode(userNode, user)),
        };
    }
    return res.data;
};

const createUserNodeApi = async (arg: CreateUserNodeRequest): Promise<UserNode> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/nodes/usernodes/`, window.location.origin);
    const { type, unassignedUserData, cuit, parents, subtype } = arg;
    const body: Record<string, unknown> = { type };
    if (unassignedUserData != null) body.unassignedUserData = unassignedUserData;
    if (cuit != null) body.cuit = cuit;
    if (parents != null) body.parents = parents;
    if (subtype != null) body.subtype = subtype;
    const res = await axFetch.post(apiUrl.toString(), body);
    return res.data;
};

const updateUserNodeApi = async ({ id, data }: { id: number; data: UpdateUserNodeRequest }): Promise<UserNode> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/nodes/usernodes/${id}/`,
        window.location.origin
    );
    const res = await axFetch.patch(apiUrl.toString(), data);
    return res.data;
};

const deleteUserNodeApi = async ({ id }: { id: number }): Promise<void> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/nodes/usernodes/${id}/`,
        window.location.origin
    );
    await axFetch.delete(apiUrl.toString());
};

const getAncestorsFromApi = async (
    { node, page_size }: { node?: number; page_size?: number },
    user?: User
): Promise<ReturnType<typeof parseUserNode>[]> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/nodes/ancestors/`, window.location.origin);

    const params = new URLSearchParams();
    if (node) params.append('node', String(node));
    if (page_size) params.append('page_size', String(page_size));
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    if (res.status === 200) {
        return res.data.map((userNode: UserNode) => parseUserNode(userNode, user));
    }
    return res.data;
};

const getSquadFromApi = async (
    { node }: { node?: number },
    user?: User
): Promise<ReturnType<typeof parseUserNode>[]> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/nodes/squad/`, window.location.origin);

    const params = new URLSearchParams();
    if (node) params.append('node', String(node));
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data.map((userNode: UserNode) => parseUserNode(userNode, user));
};

// Validated users: identity user + optional user_node (from ValidatedUserLinkSerializer)
export interface ValidatedUserLink {
    email?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    type?: string;
    subtype?: string;
    userNode?: ReturnType<typeof parseUserNode> | null;
}

// Distributor with advisors (from UserNodeWithChildrenSerializer)
export interface DistributorWithAdvisors {
    userNode: ReturnType<typeof parseUserNode>;
    children: (ReturnType<typeof parseUserNode> & { isLinked: boolean })[];
}

const getValidatedUsersRvsFromApi = async ({
    page = 1,
    page_size = 10,
    search,
}: {
    page?: number;
    page_size?: number;
    search?: string;
}): Promise<PaginatedResponse<ValidatedUserLink>> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/nodes/validated-users/rvs/`,
        window.location.origin
    );
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(page_size));
    if (search) params.append('search', search);
    apiUrl.search = params.toString();
    const res = await axFetch.get(apiUrl.toString());
    return {
        ...res.data,
        results: res.data.results.map((el: any) => {
            return {
                ...el,
                userNode: el.userNode ? parseUserNode(el.userNode) : null,
            };
        }),
    };
};

const getDistributorsAndAdvisorsFromApi = async ({
    page = 1,
    page_size = 10,
    parent_id,
    search,
}: {
    page?: number;
    page_size?: number;
    parent_id?: number;
    search?: string;
}): Promise<PaginatedResponse<DistributorWithAdvisors>> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/nodes/validated-users/distributors-and-advisors/`,
        window.location.origin
    );
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(page_size));
    if (parent_id !== undefined) params.append('parent_id', String(parent_id));
    if (search) params.append('search', search);
    apiUrl.search = params.toString();
    const res = await axFetch.get(apiUrl.toString());
    return {
        ...res.data,
        results: res.data.results.map((el) => {
            return {
                ...el,
                userNode: parseUserNode(el.userNode),
                children: el.children.map((child) => ({ ...parseUserNode(child), isLinked: Boolean(child.isLinked) })),
            };
        }),
    };
};

const advisorRvBase = () => `${(settings as any).urls.apiDomain}/core/v1/nodes/advisor-rv`;

export type CheckCuitAvailabilityResponse =
    | { status: 'available' }
    | { status: 'assigned_to_you'; userNode: UserNode }
    | { status: 'assigned_to_another_rv'; contact?: UserNode }
    | { status: 'assigned_to_another_advisor'; contact?: UserNode }
    | { status: 'assigned_to_another_advisor_with_another_rv'; contact: UserNode }
    | { status: 'assigned_to_your_rv'; contact: UserNode }
    | { status: 'assigned_to_your_advisor'; contact: UserNode };

const checkCuitAvailabilityApi = async (cuit: string): Promise<CheckCuitAvailabilityResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/nodes/check-cuit-availability/`,
        window.location.origin
    );
    apiUrl.searchParams.set('cuit', cuit);
    const res = await axFetch.get<CheckCuitAvailabilityResponse>(apiUrl.toString());
    const raw = res.data;

    if (raw.status === 'available') {
        return { status: 'available' };
    }
    if (raw.status === 'assigned_to_you') {
        return {
            status: 'assigned_to_you',
            userNode: parseUserNode(raw.userNode),
        };
    }
    return {
        ...raw,
        contact: raw.contact ? parseUserNode(raw.contact) : undefined,
    } as CheckCuitAvailabilityResponse;
};

/** Same response shape as CUIT availability; lookup is by email. */
export type CheckEmailAvailabilityResponse = CheckCuitAvailabilityResponse;

const checkEmailAvailabilityApi = async (email: string): Promise<CheckEmailAvailabilityResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/nodes/check-email-availability/`,
        window.location.origin
    );
    apiUrl.searchParams.set('email', email.trim().toLowerCase());
    const res = await axFetch.get<CheckEmailAvailabilityResponse>(apiUrl.toString());
    const raw = res.data;

    if (raw.status === 'available') {
        return { status: 'available' };
    }
    if (raw.status === 'assigned_to_you') {
        return {
            status: 'assigned_to_you',
            userNode: parseUserNode(raw.userNode),
        };
    }
    return {
        ...raw,
        contact: raw.contact ? parseUserNode(raw.contact) : undefined,
    } as CheckEmailAvailabilityResponse;
};

const getAdvisorRvInvitationsFromApi = async ({
    status,
    direction,
}: {
    status?: RVAdvisorInvitationApi['status'];
    direction?: 'sent' | 'received';
}): Promise<PaginatedResponse<RVAdvisorInvitationApi>> => {
    const apiUrl = new URL(`${advisorRvBase()}/invitations/`, window.location.origin);
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (direction) params.append('direction', direction);
    params.append('page_size', '10000');
    apiUrl.search = params.toString();
    const res = await axFetch.get(apiUrl.toString());
    return {
        ...res.data,
        results: res.data.results.map((el) => ({
            ...el,
            advisor: parseUserNode(el.advisor),
            rv: parseUserNode(el.rv),
        })),
    };
};

const createAdvisorRvInvitationApi = async ({
    userNodeId,
}: {
    userNodeId: number;
}): Promise<RVAdvisorInvitationApi> => {
    const apiUrl = new URL(`${advisorRvBase()}/invitations/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString(), { userNodeId });
    return {
        ...res.data,
        advisor: parseUserNode(res.data.advisor),
        rv: parseUserNode(res.data.rv),
    };
};

const acceptAdvisorRvInvitationApi = async (id: number): Promise<RVAdvisorInvitationApi> => {
    const apiUrl = new URL(`${advisorRvBase()}/invitations/${id}/accept/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString());
    return {
        ...res.data,
        advisor: parseUserNode(res.data.advisor),
        rv: parseUserNode(res.data.rv),
    };
};

const cancelAdvisorRvInvitationApi = async (id: number): Promise<RVAdvisorInvitationApi> => {
    const apiUrl = new URL(`${advisorRvBase()}/invitations/${id}/cancel/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString());
    return {
        ...res.data,
        advisor: parseUserNode(res.data.advisor),
        rv: parseUserNode(res.data.rv),
    };
};

const declineAdvisorRvInvitationApi = async (id: number): Promise<RVAdvisorInvitationApi> => {
    const apiUrl = new URL(`${advisorRvBase()}/invitations/${id}/decline/`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString());
    return {
        ...res.data,
        advisor: parseUserNode(res.data.advisor),
        rv: parseUserNode(res.data.rv),
    };
};

const getAdvisorRvLinksFromApi = async (): Promise<PaginatedResponse<RVAdvisorLinkApi>> => {
    const apiUrl = new URL(`${advisorRvBase()}/links/`, window.location.origin);
    apiUrl.searchParams.set('page_size', '10000');
    const res = await axFetch.get(apiUrl.toString());
    return {
        ...res.data,
        results: res.data.results.map((el) => ({
            ...el,
            advisor: parseUserNode(el.advisor),
            rv: parseUserNode(el.rv),
        })),
    };
};

export const sendFieldviewInvitationApi = async ({
    invitedUserName,
    invitedUserEmail,
    countryCode,
    sharedNodeId,
}: {
    invitedUserName: string;
    invitedUserEmail: string;
    countryCode: string;
    sharedNodeId: number;
}): Promise<{ status?: number; data?: { message?: string; error?: string } }> => {
    const apiUrl = new URL(`${(settings as any).urls.users.inviteUserNodeFieldview}`, window.location.origin);
    const res = await axFetch.post(apiUrl.toString(), {
        invited_user_name: invitedUserName,
        invited_user_email: invitedUserEmail,
        country_code: countryCode,
        shared_node_id: sharedNodeId,
    });
    return res.data;
};

export const acceptFieldviewInvitationApi = async ({ token }: { token: string }): Promise<void> => {
    const apiUrl = new URL(`${(settings as any).urls.users.acceptInvitationFieldview}`, window.location.origin);
    mpTrackEvent('FieldView Invitation Accepted', {});
    await axFetch.post(apiUrl.toString(), {
        token,
    });
};

export const listInvitationsApi = async ({
    status,
    page,
    pageSize,
}: {
    status?: 'pending' | 'accepted';
    page?: number;
    pageSize?: number;
}): Promise<PaginatedResponse<Invitation>> => {
    const apiUrl = new URL(`${(settings as any).urls.users.listInvites}`, window.location.origin);
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    if (page) queryParams.append('page', String(page));
    if (pageSize) queryParams.append('page_size', String(pageSize));
    apiUrl.search = queryParams.toString();
    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

// Hooks
export const useUserNodes = (
    {
        node,
        type,
        parent,
        parent__in,
        page = 1,
        page_size = 10,
        includeChildren = true,
        excludeTargetNode = false,
    }: {
        node?: number;
        type?: string;
        parent?: number;
        parent__in?: number[];
        includeChildren?: boolean;
        page?: number;
        page_size?: number;
        excludeTargetNode?: boolean;
    } = {},
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = {
        key: 'userNodes',
        node,
        type,
        parent,
        parent__in,
        page,
        page_size,
        includeChildren,
        excludeTargetNode,
    };
    const user = useSelector(getUser);
    // Large bulk fetches (page_size ≥ 1000) are full node-tree loads that rarely
    // change mid-session. Pass immutable options so all subscribers share the cached
    // result without re-fetching on component remount, focus, or reconnect.
    const swrOptions =
        page_size >= 1000 ? { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false } : {};
    return useSWR(enabled ? key : null, () => getUserNodesFromApi(key, user), swrOptions);
};

export const useOwnUserNode = () => {
    const userNodeQuery = useUserNodes({ includeChildren: false });
    return {
        data: userNodeQuery.data?.results?.[0],
        isValidating: userNodeQuery.isValidating,
    };
};

export const useAllUserNodesLookup = () => {
    const userNodesQuery = useUserNodes({ includeChildren: true, page_size: 10000 });
    const ancestorsQuery = useAncestors();

    const isValidatedBayerUserType = useIsValidatedBayerUserType();
    const isAdvisor = useIsValidatedAdvisor();
    const advisorRvLinksQuery = useAdvisorRvLinks({ enabled: isValidatedBayerUserType });
    const advisorRvLinksUserNodes = useMemo(() => {
        return (advisorRvLinksQuery.data?.results || []).map((link) => (isAdvisor ? link.rv : link.advisor));
    }, [advisorRvLinksQuery.data, isAdvisor]);

    const allNodesById = useMemo(() => {
        const allNodes = [
            ...(userNodesQuery.data?.results || []),
            ...(ancestorsQuery.data || []),
            ...(advisorRvLinksUserNodes || []),
        ];
        return new Map(allNodes.map((node) => [node.id, node]));
    }, [userNodesQuery.data?.results, ancestorsQuery.data, advisorRvLinksUserNodes]);

    return {
        allNodesById,
        isValidating: userNodesQuery.isValidating || ancestorsQuery.isValidating,
        isLoading: userNodesQuery.isLoading || ancestorsQuery.isLoading,
    };
};

export const useGetUserNode = (id: number) => {
    const { allNodesById, isValidating, isLoading } = useAllUserNodesLookup();
    const data = useMemo(() => allNodesById.get(id), [allNodesById, id]);
    return { data, isValidating, isLoading };
};

export const useGetUserNodes = (ids: number[]) => {
    const { allNodesById, isValidating, isLoading } = useAllUserNodesLookup();
    const data = useMemo(() => ids.map((id) => allNodesById.get(id)), [allNodesById, ids]);
    return { data, isValidating, isLoading };
};

export const useAncestors = (
    { node, types, page_size = 10000 }: { node?: number; types?: UserNode['type'][]; page_size?: number } = {},
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = {
        key: 'ancestors',
        node,
        types,
        page_size,
    };
    const user = useSelector(getUser);
    const { data, ...rest } = useSWRImmutable(enabled ? key : null, () =>
        getAncestorsFromApi(key, user as User | undefined)
    );

    const filteredData = useMemo(() => {
        if (!data || !types || types.length === 0) {
            return data;
        }
        return data.filter((userNode) => types.includes(userNode.type));
    }, [data, types]);

    return {
        data: filteredData,
        ...rest,
    };
};

/**
 * Members of a squad: a Squad Coach and its Consultant/RV/Advisor descendants.
 * Without `node`, the backend resolves the squad of the current user's node.
 */
export const useSquad = (
    { node }: { node?: number } = {},
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = { key: 'squad', node };
    const user = useSelector(getUser);
    return useSWR(enabled ? key : null, () => getSquadFromApi(key, user as User | undefined));
};

const isMagoyaEmail = (email?: string | null) => Boolean(email && email.toLowerCase().endsWith('@magoya.com'));

const shouldHideMagoyaUsers = () =>
    typeof window !== 'undefined' && !new URLSearchParams(window.location.search).has('debug');

export const useValidatedUsersRvs = (
    { page = 1, page_size = 10000, search }: { page?: number; page_size?: number; search?: string } = {},
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = { key: 'validatedUsersRvs', page, page_size, search };
    const { data, ...rest } = useSWRImmutable(enabled ? key : null, () => getValidatedUsersRvsFromApi(key));

    const filteredData = useMemo(() => {
        if (!data || !shouldHideMagoyaUsers()) return data;
        return {
            ...data,
            results: data.results.filter((el) => !isMagoyaEmail(el.email)),
        };
    }, [data]);

    return { data: filteredData, ...rest };
};

export const useDistributorsAndAdvisors = (
    {
        page = 1,
        page_size = 10000,
        parent_id,
        search,
    }: { page?: number; page_size?: number; parent_id?: number; search?: string } = {},
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = { key: 'distributorsAndAdvisors', page, page_size, parent_id, search };
    const isValidatedRV = useIsValidatedRV();
    const { data, ...rest } = useSWRImmutable(enabled && isValidatedRV ? key : null, () =>
        getDistributorsAndAdvisorsFromApi(key)
    );

    const filteredData = useMemo(() => {
        if (!data || !shouldHideMagoyaUsers()) return data;
        return {
            ...data,
            results: data.results
                .map((el) => ({
                    ...el,
                    children: el.children.filter(
                        (child) => !isMagoyaEmail(child.email) && !isMagoyaEmail(child.user?.email)
                    ),
                }))
                .filter((el) => el.children.length > 0),
        };
    }, [data]);

    return { data: filteredData, ...rest };
};

export const invalidateUserNodes = (cacheKey: any) => {
    const output =
        typeof cacheKey === 'object' &&
        cacheKey !== null &&
        ['useGrowers', 'growersDashboard', 'userNodes'].includes((cacheKey as { key?: string }).key ?? '');

    return output;
};

export const useCreateUserNode = () => {
    const { mutate } = useSWRConfig();

    return useSWRMutation(
        'createUserNode',
        (url: string, { arg }: { arg: CreateUserNodeRequest }) => createUserNodeApi(arg),
        {
            onSuccess: () => {
                mutate(invalidateUserNodes);
            },
        }
    );
};

export const useCreateMultipleUserNodes = () => {
    return useSWRMutation('createMultipleUserNodes', (url: string, { arg }: { arg: CreateUserNodeRequest[] }) => {
        return Promise.allSettled(arg.map((item) => createUserNodeApi(item)));
    });
};

export const useUpdateUserNode = () => {
    const { mutate } = useSWRConfig();

    return useSWRMutation(
        'updateUserNode',
        (url: string, { arg }: { arg: { id: number; data: UpdateUserNodeRequest } }) => updateUserNodeApi(arg),
        {
            onSuccess: (data, key, config) => {
                mutate(invalidateUserNodes);
            },
        }
    );
};

export const useDeleteUserNode = () => {
    const { mutate } = useSWRConfig();

    return useSWRMutation('deleteUserNode', (url: string, { arg }: { arg: number }) => deleteUserNodeApi({ id: arg }), {
        onSuccess: (data, key, config) => {
            mutate(invalidateUserNodes);
            enqueueSnackbar(_('Eliminaste a este productor.'), { variant: 'success' });
        },
    });
};

export const useSendFieldviewInvitation = () => {
    const { mutate } = useSWRConfig();
    const country = useSelector(getSessionCountry);

    return useSWRMutation(
        'sendFieldviewInvitation',
        (key, { arg }: { arg: { invitedUserName: string; invitedUserEmail: string; sharedNodeId: number } }) =>
            sendFieldviewInvitationApi({ ...arg, countryCode: country.code! }),
        {
            onSuccess: (data, key, config) => {
                // Invalidate invitations list cache
                mutate((cacheKey) => {
                    if (typeof cacheKey === 'object' && cacheKey !== null && 'key' in cacheKey) {
                        return (cacheKey as { key?: string }).key === 'useListInvitations';
                    }
                    return false;
                });
            },
        }
    );
};

// Advisor-RV hooks
const ADVISOR_RV_INVITATIONS_KEY = 'advisorRvInvitations';
const ADVISOR_RV_LINKS_KEY = 'advisorRvLinks';

const invalidateAdvisorRvCache = (key: string) => (cacheKey: unknown) => {
    if (typeof cacheKey === 'object' && cacheKey !== null && 'key' in (cacheKey as object)) {
        return (cacheKey as { key: string }).key === key;
    }
    return false;
};

export const useAdvisorRvInvitations = (
    {
        status,
        direction,
        dedupe = true,
    }: { status?: RVAdvisorInvitationApi['status']; direction?: 'sent' | 'received'; dedupe?: boolean } = {},
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = enabled ? { key: ADVISOR_RV_INVITATIONS_KEY, status, direction } : null;
    // Invitations don't change on focus; mutations call mutate() directly to refresh.
    const result = useSWR(key, () => getAdvisorRvInvitationsFromApi({ status, direction }), {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const deduped = useMemo(() => {
        if (!result.data || !dedupe) return result.data;
        const map = new Map<string, RVAdvisorInvitationApi>();
        for (const invitation of result.data.results) {
            const pairKey = `${invitation.advisor.id}-${invitation.rv.id}`;
            const existing = map.get(pairKey);
            if (!existing || invitation.created > existing.created) {
                map.set(pairKey, invitation);
            }
        }
        return { count: map.size, results: Array.from(map.values()) };
    }, [result.data, dedupe]);

    return { ...result, data: deduped };
};

export const useCreateAdvisorRvInvitation = () => {
    const { mutate } = useSWRConfig();
    return useSWRMutation(
        'createAdvisorRvInvitation',
        (_key: string, { arg }: { arg: { userNodeId: number } }) => createAdvisorRvInvitationApi(arg),
        {
            onSuccess: () => {
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_INVITATIONS_KEY));
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_LINKS_KEY));
            },
        }
    );
};

export const useAcceptAllAdvisorRvInvitations = () => {
    const { trigger: acceptInvitation } = useAcceptAdvisorRvInvitation({ showSnackbar: false });
    const { mutate } = useSWRConfig();
    return useSWRMutation(
        'acceptAllAdvisorRvInvitations',
        async (_key: string) => {
            const res = await getAdvisorRvInvitationsFromApi({ status: 'pending', direction: 'received' });
            const results = await Promise.all(res.results?.map((invitation) => acceptInvitation(invitation.id)) ?? []);
            return results;
        },
        {
            onSuccess: (results) => {
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_INVITATIONS_KEY));
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_LINKS_KEY));
                if (results.length > 0) {
                    enqueueSnackbar(_('Aceptaste {{count}} solicitud de vinculación', { count: results.length }), {
                        variant: 'success',
                    });
                }
            },
        }
    );
};

export const useAcceptAdvisorRvInvitation = ({ showSnackbar = true }: { showSnackbar?: boolean } = {}) => {
    const { mutate } = useSWRConfig();
    return useSWRMutation(
        'acceptAdvisorRvInvitation',
        (_key: string, { arg }: { arg: number }) => acceptAdvisorRvInvitationApi(arg),
        {
            onSuccess: (data) => {
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_INVITATIONS_KEY));
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_LINKS_KEY));
                if (showSnackbar) {
                    enqueueSnackbar(
                        _('Aceptaste la solicitud de {{advisorName}}', { advisorName: data.advisor.name }),
                        {
                            variant: 'success',
                        }
                    );
                }
            },
        }
    );
};

export const useCancelAdvisorRvInvitation = () => {
    const { mutate } = useSWRConfig();
    return useSWRMutation(
        'cancelAdvisorRvInvitation',
        (_key: string, { arg }: { arg: number }) => cancelAdvisorRvInvitationApi(arg),
        {
            onSuccess: () => {
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_INVITATIONS_KEY));
            },
        }
    );
};

export const useDeclineAdvisorRvInvitation = ({ showSnackbar = true }: { showSnackbar?: boolean } = {}) => {
    const { mutate } = useSWRConfig();
    return useSWRMutation(
        'declineAdvisorRvInvitation',
        (_key: string, { arg }: { arg: number }) => declineAdvisorRvInvitationApi(arg),
        {
            onSuccess: (data) => {
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_INVITATIONS_KEY));
                if (showSnackbar) {
                    const senderName = data.invitedBy === 'rv' ? data.rv.name : data.advisor.name;
                    enqueueSnackbar(_('Rechazaste la solicitud de {{senderName}}', { senderName }), {
                        variant: 'warning',
                    });
                }
            },
        }
    );
};

export const useDeclineAllAdvisorRvInvitations = () => {
    const { trigger: declineInvitation } = useDeclineAdvisorRvInvitation({ showSnackbar: false });
    const { mutate } = useSWRConfig();
    return useSWRMutation(
        'declineAllAdvisorRvInvitations',
        async (_key: string) => {
            const res = await getAdvisorRvInvitationsFromApi({ status: 'pending', direction: 'received' });
            const results = await Promise.all(res.results?.map((invitation) => declineInvitation(invitation.id)) ?? []);
            return results;
        },
        {
            onSuccess: (results) => {
                mutate(invalidateAdvisorRvCache(ADVISOR_RV_INVITATIONS_KEY));
                if (results.length > 0) {
                    enqueueSnackbar(_('Rechazaste {{count}} solicitud de vinculación', { count: results.length }), {
                        variant: 'warning',
                    });
                }
            },
        }
    );
};

export const useAdvisorRvLinks = ({ enabled = true }: { enabled?: boolean } = { enabled: true }) => {
    const key = enabled ? { key: ADVISOR_RV_LINKS_KEY } : null;
    // Advisor-RV relationships don't change during a session; use immutable options
    // to avoid refetching on component remount or tab focus.
    return useSWR(key, () => getAdvisorRvLinksFromApi(), {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useCheckCuitAvailability = () => {
    return useSWRMutation('checkCuitAvailability', (_key: string, { arg }: { arg: string }) =>
        checkCuitAvailabilityApi(arg)
    );
};

export const useCheckEmailAvailability = () => {
    return useSWRMutation('checkEmailAvailability', (_key: string, { arg }: { arg: string }) =>
        checkEmailAvailabilityApi(arg)
    );
};

// FV Invitations

export const useListInvitations = ({
    status,
    page = 1,
    pageSize = 10000,
}: {
    status?: 'pending' | 'accepted';
    page?: number;
    pageSize?: number;
}) => {
    return useSWR(
        {
            key: 'useListInvitations',
            status,
            page,
            pageSize,
        },
        () => listInvitationsApi({ status, page, pageSize })
    );
};

export const useListInvitationsByUser = ({ email, status }: { status?: 'pending' | 'accepted'; email: string }) => {
    const invitations = useListInvitations({ status });

    const invitationsByUser = useMemo(() => {
        return invitations.data?.results?.filter((invitation) => invitation?.invited_user_email === email);
    }, [invitations.data?.results, email]);

    return { data: invitationsByUser, isValidating: invitations.isValidating };
};

export const useInvitationByUser = ({ userNodeId }: { userNodeId: number }) => {
    const userNode = useGetUserNode(userNodeId);
    const invitationsQuery = useListInvitationsByUser({ email: userNode?.data?.email ?? '' });
    const invitationStatus = useMemo(() => {
        const fieldviewConnected = userNode.data?.user?.authProviders?.includes('fieldview');
        if (fieldviewConnected) {
            return 'connected';
        }
        const pendingInvitations = invitationsQuery.data?.filter((invitation) => invitation.status === 'pending');
        if (pendingInvitations?.length) {
            if (pendingInvitations.some((invitation) => dayjs(invitation.expires).isAfter(dayjs()))) return 'pending';
            return 'expired';
        }

        return null; // No invitation
    }, [invitationsQuery.data, userNode.data?.user?.authProviders]);
    const lastInvitation = invitationsQuery.data?.[0];
    const invitationSendedMoreThan7DaysAgo =
        invitationStatus === 'expired' || dayjs().diff(dayjs(lastInvitation?.created_at), 'day') > 7;

    return {
        invitationsQuery,
        invitationStatus: invitationStatus as 'connected' | 'pending' | 'expired' | null,
        lastInvitation,
        invitationSendedMoreThan7DaysAgo,
    };
};

export const useInvitationsForUserNodes = (userNodes: ReturnType<typeof parseUserNode>[]) => {
    const { data: allInvitations } = useListInvitations({});

    return useMemo(() => {
        if (!allInvitations?.results) return {};

        const invitationsByEmail = allInvitations.results.reduce(
            (acc, invitation) => {
                const email = invitation.invited_user_email;
                if (!acc[email]) {
                    acc[email] = [];
                }
                acc[email].push(invitation);
                return acc;
            },
            {} as Record<string, typeof allInvitations.results>
        );

        return userNodes.reduce(
            (acc, userNode) => {
                const userInvitations = invitationsByEmail[userNode.email] || [];
                const fieldviewConnected = userNode?.user?.authProviders?.includes('fieldview');

                let invitationStatus: string | null = null;
                if (fieldviewConnected) {
                    invitationStatus = 'connected';
                } else {
                    const pendingInvitations = userInvitations.filter((inv) => inv.status === 'pending');
                    if (pendingInvitations.length) {
                        const hasValidInvitation = pendingInvitations.some((inv) =>
                            dayjs(inv.expires).isAfter(dayjs())
                        );
                        invitationStatus = hasValidInvitation ? 'pending' : 'expired';
                    }
                }

                const lastInvitation = userInvitations[0];
                const invitationSendedMoreThan7DaysAgo =
                    invitationStatus === 'expired' ||
                    !lastInvitation ||
                    dayjs().diff(dayjs(lastInvitation.created_at), 'day') > 7;

                acc[userNode.id] = {
                    invitationStatus,
                    invitationSendedMoreThan7DaysAgo,
                    disabled: !invitationSendedMoreThan7DaysAgo,
                };

                return acc;
            },
            {} as Record<
                number,
                { invitationStatus: string | null; invitationSendedMoreThan7DaysAgo: boolean; disabled: boolean }
            >
        );
    }, [allInvitations?.results, userNodes]);
};
